<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import isEqual from 'lodash-es/isEqual'
import { useBreakpoints, checkIsKeyupEsc } from '@/lib/utils'
import BsDrawerPanel from '@/legacy/components/DrawerPanel.vue'
import {
	generateUniqueId,
	addOverlayListItem,
	removeOverlayListItem,
} from '@/legacy/ts/overlay'

const props = defineProps({
	value: {
		type: [String, Number, Array],
		required: true,
	},
	title: {
		type: String,
		required: true,
	},
	options: {
		type: [Array, Object],
	},
	isShowPanel: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['update:value', 'hide', 'change', 'finishSelect'])
const currentValue = ref(null)
const currentLabel = ref(null)
const { isLarge: isDesktop } = useBreakpoints()
const uniqueId = generateUniqueId()

const optionsMain = computed(() => {
	if (!props.options) return []
	if (Array.isArray(props.options)) {
		return props.options.map(v => ({
			label: v.label,
			value: v.value,
		}))
	}
	if (typeof props.options === 'object') {
		return Object.keys(props.options).map(key => ({
			// @ts-ignore
			label: props.options[key],
			value: key,
		}))
	}
	return []
})

function hidePanel() {
	emit('hide')
	document.removeEventListener('keyup', pressESC)
	removeOverlayListItem(uniqueId)
}
function onSelect(value: any, label: any) {
	currentValue.value = value
	currentLabel.value = label
	hidePanel()

	emit('finishSelect', value)
}
function pressESC(evt: KeyboardEvent) {
	if (checkIsKeyupEsc(evt)) {
		emit('hide')
	}
}

function syncIsShowPanel(value: boolean) {
	emit('change', value)
}

watch(
	() => props.isShowPanel,
	val => {
		emit('change', val)
		if (val) {
			document.addEventListener('keyup', pressESC)
			addOverlayListItem(uniqueId)
		}
	},
	{
		immediate: true,
	},
)
watch(
	() => props.value,
	val => {
		// @ts-ignore
		currentValue.value = val
	},
	{
		immediate: true,
	},
)

watch(
	() => currentValue.value,
	value => {
		if (
			value === '' ||
			value === null ||
			// @ts-ignore
			(Array.isArray(value) && value.length <= 0)
		) {
			currentLabel.value = null
			return false
		}

		const activeOption = optionsMain.value.find(v => isEqual(v.value, value))

		if (activeOption) {
			currentLabel.value = activeOption.label
		} else {
			currentLabel.value = value
		}

		return currentLabel.value
	},
)
</script>

<template>
	<Teleport to="body">
		<transition name="bs-selector-zoom-in-out">
			<div class="bs-select-panel" v-if="isDesktop && isShowPanel">
				<div class="bs-select-panel__overlay" @click="hidePanel()"></div>
				<div class="bs-select-panel__main">
					<div class="bs-select-panel__main__header">
						<i @click="hidePanel()" class="bs-icon bs-icon--close"></i>
						<div class="bs-select-panel__main__header__title">
							{{ title }}
						</div>
					</div>
					<div class="bs-select-panel__main__option-container">
						<div
							class="bs-select-panel__main__option"
							:class="{
								'bs-select-panel__main__option--selected': value === v.value,
							}"
							v-for="v in optionsMain"
							:value="v.value"
							:key="`d_option_${v.value}`"
							@click.stop="onSelect(v.value, v.label)"
						>
							<span class="bs-select-panel__main__option__text">
								{{ v.label }}
							</span>
						</div>
					</div>
				</div>
			</div>
		</transition>
		<BsDrawerPanel
			class="bs-select-panel-mobile"
			v-if="!isDesktop"
			:modelValue="isShowPanel"
			@update:modelValue="syncIsShowPanel"
		>
			<div class="bs-select-panel-mobile__main">
				<a
					class="bs-select-panel-mobile__option"
					:class="{
						'bs-select-panel-mobile__option--selected': value === v.value,
					}"
					href="javascript:;"
					v-for="v in optionsMain"
					:key="`m_option_${v.value}`"
					@click.stop="onSelect(v.value, v.label)"
				>
					{{ v.label }}
				</a>
			</div>
		</BsDrawerPanel>
	</Teleport>
</template>
